"use client";
import { useState } from "react";
import { Spinner } from "@clarityos/ui/spinner";
import { Drawer } from "@clarityos/ui/drawer";
import { CardCollectSection } from "./CardCollectSection";
import {
  PatientDetailsProvider,
  usePatientDetailsContext,
} from "../../context/PatientDetailsContext";
import {
  PatientDrawerTitle,
  PatientDrawerTitleSkeleton,
} from "./PatientDrawerTitle";
import { PatientMeta, PatientMetaSkeleton } from "../PatientMeta";
import { Avatar, Skeleton } from "@clarityos/ui";
import { Patient } from "../../types";
import { useTenantRouteContext } from "@/components/app-shell/TenantRouteContext";
import { usePatientChannel } from "@/lib/signalr/usePatientChannel";
import { usePaymentReceivedNotice } from "@/features/patient-ar/hooks/usePaymentReceivedNotice";
import { PaymentReceivedDialog } from "@/features/patient-ar/components/PaymentReceivedDialog";
import {
  ResendNotificationMenu,
  useResendNotification,
} from "@/features/resend-notification";

interface PatientDetailsDrawerProps {
  patientId: string | null;
  initialData?: Patient;
  onClose: () => void;
}

function PatientDetailsDrawerInner({
  onClose,
}: Pick<PatientDetailsDrawerProps, "onClose">) {
  const { patientId, patient, isLoading } = usePatientDetailsContext();

  const [linkSent, setLinkSent] = useState(false);
  const { tenantId } = useTenantRouteContext();
  usePatientChannel(tenantId, patientId);
  const { notice, dismiss } = usePaymentReceivedNotice(patientId);
  const {
    isEnabled: isResendEnabled,
    options: resendOptions,
    isSending,
    resend,
  } = useResendNotification(patientId);

  const fullName = patient
    ? `${patient.firstName ?? ""} ${patient.lastName ?? ""}`.trim()
    : "";

  const handleClose = () => {
    setLinkSent(false);
    onClose();
  };

  return (
    <Drawer
      open={patientId !== null}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <Drawer.Content
        aria-label="Patient details"
        data-testid="patient-details-drawer"
      >
        <Drawer.Header>
          <div className="flex items-center gap-4">
            <Skeleton active={isLoading}>
              <Avatar name={fullName} size="lg" />
            </Skeleton>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <Drawer.Title>
                {isLoading ? (
                  <PatientDrawerTitleSkeleton />
                ) : (
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <PatientDrawerTitle>{fullName}</PatientDrawerTitle>
                    {isResendEnabled && (
                      <ResendNotificationMenu
                        options={resendOptions}
                        isSending={isSending}
                        onSend={resend}
                      />
                    )}
                  </div>
                )}
              </Drawer.Title>
              <Drawer.Description>
                {isLoading || !patient ? (
                  <PatientMetaSkeleton />
                ) : (
                  <PatientMeta patient={patient} />
                )}
              </Drawer.Description>
            </div>
          </div>
        </Drawer.Header>
        <Drawer.Body>
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          )}

          {patient && !isLoading && <CardCollectSection linkSent={linkSent} />}
        </Drawer.Body>
      </Drawer.Content>
      <PaymentReceivedDialog
        patientId={patientId}
        notice={notice}
        onDismiss={dismiss}
      />
    </Drawer>
  );
}

export function PatientDetailsDrawer({
  patientId,
  initialData,
  onClose,
}: PatientDetailsDrawerProps) {
  return (
    <PatientDetailsProvider
      patientId={patientId}
      initialData={initialData}
      onError={onClose}
    >
      <PatientDetailsDrawerInner onClose={onClose} />
    </PatientDetailsProvider>
  );
}
